import { Component, ElementRef, OnDestroy, OnInit, ViewEncapsulation, inject, viewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NgbCarouselConfig } from '@ng-bootstrap/ng-bootstrap';
import { NgbCarousel } from '@ng-bootstrap/ng-bootstrap';
import { NgbSlideEvent } from '@ng-bootstrap/ng-bootstrap/carousel/carousel';
import { filter, ReplaySubject, takeUntil } from 'rxjs';

import { isUndefined, notUndefined } from 'app/utils/stream-util';
import CategoryConfig from 'entity/CategoryConfig';
import Conversation from 'entity/Conversation';
import { SocialEventType } from 'messages/websocket.messages';

import { VideoChatService } from '../services/videochat.service';
import SocialStateMachine from '../social-state-machine/social-state-machine';

export interface ModalConvoData {
  modalTitle: string;
  primaryButtonText?: string;
}

@Component({
  selector: 'app-modal-convo',
  templateUrl: './modal-convo.component.html',
  styleUrls: ['./modal-convo.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ModalConvoComponent implements OnInit, OnDestroy {
  protected data = inject<ModalConvoData>(MAT_DIALOG_DATA);
  private dialogRef = inject<MatDialogRef<ModalConvoComponent>>(MatDialogRef);
  private videoChatService = inject(VideoChatService);

  public topCloseEnabled = true;
  public selectedCategory?: CategoryConfig;
  public categoryList: CategoryConfig[] = [];
  private categoryCustomConvo: CategoryConfig = {
    categoryId: -1,
    description: 'Create your own Convo',
    name: 'Create your own Convo',
  };
  public createOwnConvo = false;
  public ownConvoDescription = '';
  private readonly ownConvoDescriptionLimit = 120;
  public ownConvoError = '';

  public selectedConvo?: Conversation;
  public selectedConvoIndex = 0;
  public convoList: Conversation[] = [];
  public showLoader = false;
  public canStartConvo = false;

  private readonly customConvoElement = viewChild<ElementRef>('customconvo');
  private readonly carousel = viewChild<NgbCarousel>('carousel');

  private destroyed$ = new ReplaySubject<boolean>(1);

  public constructor() {
    const carouselConfig = inject(NgbCarouselConfig);

    carouselConfig.interval = 0;
    carouselConfig.wrap = true;
    carouselConfig.keyboard = true;
  }

  public ngOnInit(): void {
    this.showLoader = true;

    // Fetch Categories
    this.buildCategoriesForm();
    SocialStateMachine.shared.stateChanged$.pipe(takeUntil(this.destroyed$)).subscribe((res) => {
      if (res !== null) {
        this.canStartConvo = res.eventType === SocialEventType.NONE;
      }
    });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  //
  private buildCategoriesForm(): void {
    this.videoChatService
      .getCategories()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result) => {
        this.showLoader = false;
        this.categoryList = [...result, this.categoryCustomConvo];
      });
  }

  public categoryChanged(): void {
    this.selectedConvo = undefined;
    this.showLoader = true;

    // Create own Convo
    if (this.selectedCategory?.categoryId === this.categoryCustomConvo.categoryId) {
      this.createOwnConvo = true;
      this.convoList = [];
      this.showLoader = false;
      setTimeout(() => {
        this.customConvoElement()?.nativeElement.focus();
      }, 800);

      // Show convo carousel
    } else {
      this.ownConvoDescription = '';
      this.createOwnConvo = false;
      this.ownConvoError = '';
      this.refreshConvosList();
    }
  }

  private refreshConvosList(): void {
    const { categoryId } = this.selectedCategory ?? {};
    if (isUndefined(categoryId)) {
      return;
    }
    this.videoChatService
      .getConversations(categoryId)
      .pipe(filter(notUndefined), takeUntil(this.destroyed$))
      .subscribe((result) => {
        this.showLoader = false;
        this.convoList = result;
        this.selectedConvo = result[0];
      });
  }

  // Listener
  public onSlide(event: NgbSlideEvent): void {
    if (this.convoList && event && event.current) {
      const selectedConvoIndex = parseInt(event.current.replace('slideId_', ''), 10);
      this.selectedConvoIndex = selectedConvoIndex;
      this.selectedConvo = this.convoList[selectedConvoIndex];
    }
  }

  public customConvoChanged(): void {
    this.ownConvoError = this.ownConvoDescription.length > this.ownConvoDescriptionLimit ? 'Over Character Limit' : '';
  }

  // Close / Confirm
  public closeDialog(): void {
    this.dialogRef.close(null);
  }

  protected changeConvo(to: 'prev' | 'next' | number) {
    const carousel = this.carousel();
    if (!carousel) {
      return;
    }
    if (typeof to == 'string') {
      carousel[to]();
      return;
    }
    carousel.select(`slideId_${to}`);
    this.selectedConvoIndex = to;
  }

  public startConvo(): void {
    // Own Convo
    if (this.createOwnConvo) {
      this.dialogRef.close({
        conversationId: -1,
        title: this.ownConvoDescription,
        description: this.ownConvoDescription,
      });

      // Selected Convo
    } else {
      this.dialogRef.close(this.selectedConvo);
    }
  }
}
